import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { getClaims, reviewClaim, getMemberships, changeMemberRole } from '../services/membershipService';
import { getProjects, createProject } from '../services/projectService';
import { updateTenantBranding, updateModuleConfig, updateTenantSettings } from '../services/tenantService';

const STATUS_LABELS = {
  unverified:          'Unverified',
  self_declared:       'Self-Declared',
  community_pending:   'Pending',
  community_verified:  'Verified',
  revoked:             'Revoked',
  // CommunityMembership statuses
  pending:   'Pending',
  active:    'Active',
  suspended: 'Suspended',
};

// ─── ProjectsTab ──────────────────────────────────────────────────────────────
const ProjectsTab = ({ tenantId }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState({ title: '', description: '', type: 'public' });
  const [msg, setMsg]           = useState('');

  useEffect(() => {
    getProjects(tenantId)
      .then((r) => setProjects(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await createProject({ ...form, tenantId });
      setProjects((prev) => [res.data, ...prev]);
      setForm({ title: '', description: '', type: 'public' });
      setMsg('Project created.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create project');
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  return (
    <section>
      <h3>Create Project</h3>
      {msg && <p className={msg.includes('Failed') ? 'error-msg' : 'success-msg'}>{msg}</p>}
      <form onSubmit={handleSubmit}>
        <input
          name="title"
          placeholder="Project Title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
        <textarea
          name="description"
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <select
          name="type"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          <option value="public">Public</option>
          <option value="community">Community</option>
          <option value="hybrid">Hybrid</option>
        </select>
        <button type="submit">Create Project</button>
      </form>

      <h3>All Projects</h3>
      {projects.length === 0 ? (
        <p>No projects yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Title</th><th>Type</th><th>Status</th></tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p._id}>
                <td>{p.title}</td>
                <td>{p.type}</td>
                <td>{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── MembershipTab ────────────────────────────────────────────────────────────
const MembershipTab = ({ tenantId }) => {
  const [claims, setClaims]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [reviewError, setError]   = useState('');

  useEffect(() => {
    getClaims({ tenantId })
      .then((r) => setClaims(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleReview = async (claimId, statusAssigned) => {
    setError('');
    try {
      const res = await reviewClaim(claimId, { statusAssigned });
      setClaims((prev) => prev.map((c) => (c._id === claimId ? res.data.claim : c)));
    } catch (err) {
      setError(err.response?.data?.message || 'Review failed');
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  return (
    <section>
      <h3>Membership Claims ({claims.length})</h3>
      {reviewError && <div className="error-msg">{reviewError}</div>}
      {claims.length === 0 ? (
        <p>No claims yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Status</th><th>Submitted</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {claims.map((c) => {
              const status = c.resolvedStatus || c.status;
              return (
                <tr key={c._id}>
                  <td>{c.userId?.email || c.userId}</td>
                  <td><span className={`status-badge status-${status}`}>{STATUS_LABELS[status] || status}</span></td>
                  <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td className="claim-actions">
                    {status !== 'community_verified' && status !== 'active' && (
                      <button onClick={() => handleReview(c._id, 'community_verified')}>Verify</button>
                    )}
                    {status !== 'revoked' && (
                      <button className="btn-danger" onClick={() => handleReview(c._id, 'revoked')}>Revoke</button>
                    )}
                    {status !== 'community_pending' && status !== 'pending' && (
                      <button className="btn-secondary" onClick={() => handleReview(c._id, 'community_pending')}>Set Pending</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

// ─── ModuleConfigTab ──────────────────────────────────────────────────────────
const MODULES = [
  { key: 'consultation',  label: 'Consultation',    description: 'Community questionnaires and feedback consultations' },
  { key: 'communityNews', label: 'Community News',  description: 'News posts and community announcements' },
  { key: 'harvesting',    label: 'Harvesting',      description: 'Harvest observation records and maps' },
  { key: 'communityData', label: 'Community Data',  description: 'Community-submitted data and consent exports' },
  { key: 'landValues',    label: 'Land Values',     description: 'Community land value criteria and scoring' },
  { key: 'landUnits',     label: 'Land Units',      description: 'Land unit assessments and evaluations' },
];

// Modules that have a separate staff reviewer panel that admins can restrict
const PANEL_ACCESS_MODULES = [
  { key: 'harvesting',   label: 'Harvesting reviewer panel' },
  { key: 'consultation', label: 'Consultation manager panel' },
];

const ModuleConfigTab = ({ tenantId, activeTenant }) => {
  const mc = activeTenant?.moduleConfig || {};

  const [config, setConfig] = useState(() => {
    const init = {};
    MODULES.forEach(({ key }) => { init[key] = mc[key]?.enabled ?? false; });
    return init;
  });

  // staffReviewAccess: whether community_staff see the reviewer/manager panel (defaults true)
  const [panelConfig, setPanelConfig] = useState({
    harvesting:   mc.harvesting?.staffReviewAccess   ?? true,
    consultation: mc.consultation?.staffReviewAccess ?? true,
  });

  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleToggle      = (key) => setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  const handlePanelToggle = (key) => setPanelConfig((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    setMsg('');
    setSaving(true);
    try {
      // Build nested per-module objects so the backend deep-merges correctly,
      // preserving externalUrl and other fields not managed here.
      const moduleConfig = {};
      MODULES.forEach(({ key }) => {
        moduleConfig[key] = { enabled: config[key] };
        if (key === 'harvesting')   moduleConfig[key].staffReviewAccess = panelConfig.harvesting;
        if (key === 'consultation') moduleConfig[key].staffReviewAccess = panelConfig.consultation;
      });
      await updateModuleConfig(tenantId, { moduleConfig });
      setMsg('Module settings saved.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <h3>Module Configuration</h3>
        <button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
      {msg && <p className={msg.includes('aved') ? 'success-msg' : 'error-msg'}>{msg}</p>}

      {/* ── Module on/off ── */}
      <p style={{ color: '#64748b', marginBottom: 16, fontSize: '0.9rem' }}>
        Enable or disable modules for your community members.
        Platform-level tools (Lenses, 7 Generation) are always available to CIP staff regardless of these settings.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {MODULES.map(({ key, label, description }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
            <input
              type="checkbox"
              checked={config[key]}
              onChange={() => handleToggle(key)}
              style={{ marginTop: 3, accentColor: '#4285F4', width: 16, height: 16 }}
            />
            <div>
              <strong style={{ fontSize: '0.95rem' }}>{label}</strong>
              <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>{description}</p>
            </div>
          </label>
        ))}
      </div>

      {/* ── Staff Panel Access ── */}
      <div style={{ marginTop: 28 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: '0.97rem', color: '#1e293b' }}>Staff Panel Access</h4>
        <p style={{ color: '#64748b', marginBottom: 12, fontSize: '0.85rem' }}>
          Control whether <strong>community staff</strong> members see the reviewer panel for these
          modules, or fall back to the standard member view.
          Community admins always retain full access.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {PANEL_ACCESS_MODULES.map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
              background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, padding: '12px 16px' }}>
              <input
                type="checkbox"
                checked={panelConfig[key]}
                onChange={() => handlePanelToggle(key)}
                style={{ marginTop: 3, accentColor: '#4285F4', width: 16, height: 16 }}
              />
              <div>
                <strong style={{ fontSize: '0.92rem' }}>Staff can access: {label}</strong>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                  {panelConfig[key]
                    ? 'Staff members see the full reviewer panel for this module'
                    : 'Staff members see the standard member view for this module'}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── BrandingTab ──────────────────────────────────────────────────────────────
const BrandingTab = ({ tenantId, activeTenant }) => {
  const [form, setForm] = useState({
    displayName:  activeTenant?.branding?.displayName  || '',
    logoUrl:      activeTenant?.branding?.logoUrl       || '',
    primaryColor: activeTenant?.branding?.primaryColor  || '',
    bannerText:   activeTenant?.branding?.bannerText    || '',
    websiteUrl:   activeTenant?.branding?.websiteUrl    || '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    setSaving(true);
    try {
      await updateTenantBranding(tenantId, form);
      setMsg('Branding updated successfully.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <h3>Community Branding</h3>
      <p className="fieldset-hint">
        These settings control how your community appears to members and on auto-generated join pages.
      </p>
      <form className="branding-form" onSubmit={handleSubmit}>
        <label>
          Display Name
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder={activeTenant?.name}
          />
          <small>Overrides the technical tenant name in all UI.</small>
        </label>
        <label>
          Logo URL
          <input
            type="url"
            value={form.logoUrl}
            onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            placeholder="https://..."
          />
        </label>
        {form.logoUrl && (
          <img src={form.logoUrl} alt="Logo preview" style={{ maxHeight: 80, maxWidth: 300, objectFit: 'contain', marginBottom: 12 }} />
        )}
        <label>
          Primary Colour (hex)
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="text"
              value={form.primaryColor}
              onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
              placeholder="#2B6CB0"
              style={{ flex: 1 }}
            />
            {form.primaryColor && (
              <span
                style={{
                  display: 'inline-block',
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: form.primaryColor,
                  border: '1px solid #ccc',
                }}
              />
            )}
          </div>
        </label>
        <label>
          Banner Text
          <input
            type="text"
            value={form.bannerText}
            onChange={(e) => setForm({ ...form, bannerText: e.target.value })}
            placeholder="Welcome to our community portal"
          />
        </label>
        <label>
          Website URL
          <input
            type="url"
            value={form.websiteUrl}
            onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
            placeholder="https://your-community-website.com"
          />
          <small>Link users back to your main community website.</small>
        </label>
        {msg && (
          <p className={msg.includes('successfully') ? 'success-msg' : 'error-msg'}>{msg}</p>
        )}
        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save Branding'}
          </button>
        </div>
      </form>
    </section>
  );
};

// ─── MembersTab ───────────────────────────────────────────────────────────────
const ROLE_LABELS = { member: 'Member', community_staff: 'Staff', community_admin: 'Admin' };
const STATUS_BADGE_MAP = { active: 'status-active', pending: 'status-pending', suspended: 'status-suspended', revoked: 'status-revoked' };

const MembersTab = ({ tenantId }) => {
  const [members, setMembers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [msg, setMsg]                 = useState('');
  const [roleEdits, setRoleEdits]     = useState({});   // membershipId -> selected role
  const [roleSaving, setRoleSaving]   = useState({});   // membershipId -> bool

  useEffect(() => {
    getMemberships({ tenantId })
      .then((r) => setMembers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleRoleChange = (membershipId, newRole) => {
    setRoleEdits((prev) => ({ ...prev, [membershipId]: newRole }));
  };

  const handleRoleSave = async (membershipId) => {
    const newRole = roleEdits[membershipId];
    setMsg('');
    setRoleSaving((prev) => ({ ...prev, [membershipId]: true }));
    try {
      const res = await changeMemberRole(membershipId, newRole);
      setMembers((prev) =>
        prev.map((m) => (m._id === membershipId ? { ...m, communityRole: res.data.communityRole } : m))
      );
      setRoleEdits((prev) => { const n = { ...prev }; delete n[membershipId]; return n; });
    } catch (err) {
      setMsg(err.response?.data?.message || 'Role change failed');
    } finally {
      setRoleSaving((prev) => ({ ...prev, [membershipId]: false }));
    }
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  const active  = members.filter((m) => m.status === 'active');
  const pending = members.filter((m) => m.status === 'pending');
  const other   = members.filter((m) => !['active', 'pending'].includes(m.status));

  return (
    <section>
      <h3>Community Members ({members.length})</h3>
      {msg && <p className="error-msg">{msg}</p>}

      {pending.length > 0 && (
        <div className="pending-section">
          <h4>⏳ Pending ({pending.length})</h4>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Since</th></tr></thead>
            <tbody>
              {pending.map((m) => (
                <tr key={m._id}>
                  <td>{m.userId?.email}</td>
                  <td>{m.userId?.firstName} {m.userId?.lastName}</td>
                  <td>{ROLE_LABELS[m.communityRole] || m.communityRole}</td>
                  <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="fieldset-hint">
            To approve pending members, use the <strong>Membership</strong> tab to review their claims.
          </p>
        </div>
      )}

      <h4>Active Members ({active.length})</h4>
      {active.length === 0 ? (
        <p>No active members yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Email</th><th>Name</th><th>Role</th><th>Joined</th><th>Change Role</th></tr>
          </thead>
          <tbody>
            {active.map((m) => {
              const editing = roleEdits[m._id] !== undefined;
              const currentRole = roleEdits[m._id] ?? m.communityRole;
              return (
                <tr key={m._id}>
                  <td>{m.userId?.email}</td>
                  <td>{m.userId?.firstName} {m.userId?.lastName}</td>
                  <td>
                    <span className={`status-badge ${STATUS_BADGE_MAP[m.communityRole] || ''}`}>
                      {ROLE_LABELS[m.communityRole] || m.communityRole}
                    </span>
                  </td>
                  <td>{m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <select
                      value={currentRole}
                      onChange={(e) => handleRoleChange(m._id, e.target.value)}
                      style={{ marginRight: 6, fontSize: '0.82rem' }}
                    >
                      <option value="member">Member</option>
                      <option value="community_staff">Staff</option>
                      <option value="community_admin">Admin</option>
                    </select>
                    {editing && (
                      <button
                        className="btn-sm"
                        disabled={roleSaving[m._id]}
                        onClick={() => handleRoleSave(m._id)}
                      >
                        {roleSaving[m._id] ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {other.length > 0 && (
        <>
          <h4>Other ({other.length})</h4>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Status</th></tr></thead>
            <tbody>
              {other.map((m) => (
                <tr key={m._id}>
                  <td>{m.userId?.email}</td>
                  <td><span className={`status-badge ${STATUS_BADGE_MAP[m.status] || ''}`}>{m.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};

// ─── SettingsTab ─────────────────────────────────────────────────────────────
const SettingsTab = ({ tenantId, activeTenant }) => {
  const [listed, setListed] = useState(activeTenant?.settings?.listInDirectory ?? false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await updateTenantSettings(tenantId, { listInDirectory: listed });
      setMsg('Settings saved.');
    } catch (err) {
      setMsg(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const slug = activeTenant?.slug || '';
  const joinUrl = slug ? `${window.location.origin}/t/${slug}/join` : null;

  return (
    <div>
      <div className="section-header">
        <h3>Community Settings</h3>
        <button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
      {msg && <p className={msg.includes('aved') ? 'success-msg' : 'error-msg'}>{msg}</p>}

      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: '0.97rem', color: '#1e293b' }}>Public Directory Listing</h4>
        <p style={{ color: '#64748b', marginBottom: 12, fontSize: '0.85rem' }}>
          When enabled, your community appears in the public directory on the Membership page.
          Anyone can find and apply to join. Disable to keep your community private —
          members join via your direct link below.
        </p>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' }}>
          <input
            type="checkbox"
            checked={listed}
            onChange={e => setListed(e.target.checked)}
            style={{ marginTop: 3, accentColor: '#4285F4', width: 16, height: 16 }}
          />
          <div>
            <strong style={{ fontSize: '0.95rem' }}>List in public directory</strong>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
              {listed
                ? 'Your community is visible in the public directory'
                : 'Your community is private — share your join link directly with members'}
            </p>
          </div>
        </label>
      </div>

      {joinUrl && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 6px', fontSize: '0.97rem', color: '#1e293b' }}>Direct Join Link</h4>
          <p style={{ color: '#64748b', marginBottom: 8, fontSize: '0.85rem' }}>
            Share this link with your members. Anyone with the link can apply to join.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{
              background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6,
              padding: '6px 12px', fontSize: '0.82rem', wordBreak: 'break-all', flex: 1,
            }}>{joinUrl}</code>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(joinUrl); }}
              style={{
                background: '#4285F4', border: 'none', borderRadius: 6,
                color: '#fff', padding: '6px 14px', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
              }}
            >
              Copy Link
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 6 }}>
            Community slug: <strong>{slug}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

// ─── CommunityAdminPage ───────────────────────────────────────────────────────
const CommunityAdminPage = () => {
  const { viewTenant, loading: tenantLoading, error: tenantError } = useTenant();
  const tenantId = viewTenant?._id;

  const [activeTab, setActiveTab] = useState('projects');

  if (tenantLoading) {
    return (
      <div className="page community-admin-page">
        <p className="page-loading">Loading community…</p>
      </div>
    );
  }

  if (tenantError || !tenantId) {
    return (
      <div className="page community-admin-page">
        <p className="page-loading">{tenantError || 'Community not found.'}</p>
      </div>
    );
  }

  return (
    <div className="page community-admin-page">
      <h2>Community Admin — {viewTenant?.branding?.displayName || viewTenant?.name}</h2>

      <div className="tabs">
        <button className={activeTab === 'projects'   ? 'active' : ''} onClick={() => setActiveTab('projects')}>
          Projects
        </button>
        <button className={activeTab === 'members'    ? 'active' : ''} onClick={() => setActiveTab('members')}>
          Members
        </button>
        <button className={activeTab === 'membership' ? 'active' : ''} onClick={() => setActiveTab('membership')}>
          Claims
        </button>
        <button className={activeTab === 'branding'   ? 'active' : ''} onClick={() => setActiveTab('branding')}>
          Branding
        </button>
        <button className={activeTab === 'modules'   ? 'active' : ''} onClick={() => setActiveTab('modules')}>
          Modules
        </button>
        <button className={activeTab === 'settings'  ? 'active' : ''} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
      </div>

      {activeTab === 'projects'   && <ProjectsTab      tenantId={tenantId} />}
      {activeTab === 'members'    && <MembersTab        tenantId={tenantId} />}
      {activeTab === 'membership' && <MembershipTab     tenantId={tenantId} />}
      {activeTab === 'branding'   && <BrandingTab       tenantId={tenantId} activeTenant={viewTenant} />}
      {activeTab === 'modules'    && <ModuleConfigTab   tenantId={tenantId} activeTenant={viewTenant} />}
      {activeTab === 'settings'   && <SettingsTab       tenantId={tenantId} activeTenant={viewTenant} />}
    </div>
  );
};

export default CommunityAdminPage;
